import * as React from 'react';
import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { colors, shadows } from '../../employees/styles/personalakteStyles';

interface Location {
  id: number;
  name: string;
}

interface LocationFilterProps {
  locations: Location[];
  selectedLocations: number[];
  onSelectedLocationsChange: (locations: number[]) => void;
}

function LocationFilter({ locations, selectedLocations, onSelectedLocationsChange }: LocationFilterProps) {
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const locationDropdownRef = useRef<HTMLDivElement>(null);
  const locationPortalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (locationDropdownRef.current?.contains(target)) return;
      if (locationPortalRef.current?.contains(target)) return;
      setLocationDropdownOpen(false);
    };
    if (locationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [locationDropdownOpen]);

  if (locations.length <= 1) return null;

  const activeCount = selectedLocations.length;
  const currentLabel = activeCount === 0
    ? 'Alle Standorte'
    : `Standorte (${activeCount}/${locations.length})`;

  const toggleLocation = (locId: number) => {
    if (selectedLocations.includes(locId)) {
      onSelectedLocationsChange(selectedLocations.filter(id => id !== locId));
    } else {
      onSelectedLocationsChange([...selectedLocations, locId]);
    }
  };

  return (
    <div ref={locationDropdownRef} style={{ position: 'relative', marginRight: '8px' }}>
      <button
        onClick={() => setLocationDropdownOpen(prev => !prev)}
        style={{
          padding: '6px 14px',
          borderRadius: '9999px',
          border: activeCount > 0 ? '1px solid rgba(82, 113, 165, 0.3)' : '1px solid rgba(0, 0, 0, 0.08)',
          background: activeCount > 0 ? 'rgba(82, 113, 165, 0.4)' : 'rgba(255, 255, 255, 0.8)',
          color: activeCount > 0 ? colors.slate : colors.navy,
          fontSize: '11px',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          boxShadow: shadows.soft,
        }}
      >
        {currentLabel}
        <span style={{ fontSize: '8px' }}>▼</span>
      </button>

      {locationDropdownOpen && createPortal(
        <div ref={locationPortalRef} style={{
          position: 'fixed',
          top: (() => { const r = locationDropdownRef.current?.getBoundingClientRect(); return r ? r.bottom + 4 : 0; })(),
          left: (() => { const r = locationDropdownRef.current?.getBoundingClientRect(); return r ? r.left : 0; })(),
          background: colors.bgCard,
          borderRadius: '16px',
          padding: '16px',
          minWidth: '280px',
          boxShadow: shadows.elevated,
          border: `1px solid ${colors.border}`,
          zIndex: 9999,
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {locations.map(loc => {
              const isActive = selectedLocations.includes(loc.id);
              return (
                <div
                  key={loc.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleLocation(loc.id)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleLocation(loc.id); }}
                  style={{
                    padding: '7px 16px',
                    borderRadius: '9999px',
                    border: isActive ? `2px solid ${colors.slate}` : '2px solid #e2e8f0',
                    background: isActive ? colors.slate : 'transparent',
                    color: isActive ? 'white' : '#94a3b8',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {loc.name}
                </div>
              );
            })}
          </div>
          {activeCount > 0 && (
            <button
              onClick={() => onSelectedLocationsChange([])}
              style={{
                marginTop: '12px',
                padding: '4px 8px',
                borderRadius: '9999px',
                border: 'none',
                background: 'transparent',
                color: colors.textMuted,
                fontSize: '10px',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'center',
              }}
            >
              Alle anzeigen
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

export default LocationFilter;
